// T3-B PR 4-bis (2026-05-12):
//   motif_template_sets 一覧 + 編集画面。
//
// Spec: design/sponsored-motif-spec.md v3 §6.6

import { Button } from '@/components/common/Button';
import { useStore } from '@/hooks/useStore';
import { toastify } from '@/utils/toastify';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2 } from 'lucide-react';
import MotifTemplateSetFormSheet from './components/MotifTemplateSetFormSheet';

export default observer(function MotifTemplateSetManagementPage() {
    const { t } = useTranslation();
    const {
        tierCatalogStore: { templateSets, listTemplateSets, removeTemplateSet },
        modalStore: { showAlertModal }
    } = useStore();

    const [sheetOpen, setSheetOpen] = useState(false);
    const [sheetMode, setSheetMode] = useState<'create' | 'edit'>('create');
    const [editTarget, setEditTarget] = useState<any | null>(null);

    const fetchList = () => flowResult(listTemplateSets({ include_inactive: 1 }));

    useEffect(() => {
        fetchList();
    }, []);

    const handleOpenCreate = () => {
        setSheetMode('create');
        setEditTarget(null);
        setSheetOpen(true);
    };

    const handleOpenEdit = (templateSet: any) => {
        setSheetMode('edit');
        setEditTarget(templateSet);
        setSheetOpen(true);
    };

    const handleDelete = (templateSet: any) => {
        showAlertModal({
            title: t('words_title.confirm_delete') || '削除確認',
            size: 'sm',
            content: `「${templateSet.name}」を削除しますか?\n参照中の landmark / sponsored_motif があると 409 で拒否されます (= is_active=false 代替推奨)。`,
            onSave: async () => {
                const res = await flowResult(removeTemplateSet(templateSet.id));
                if (res?.ok) {
                    toastify('success', '削除しました');
                    fetchList();
                } else {
                    toastify('error', res?.message || '削除に失敗しました');
                }
            }
        });
    };

    return (
        <div className='layout-card'>
            <div className='flex items-center justify-between mb-4'>
                <div>
                    <h2 className='text-[18px] font-bold'>{t('words_title.motif_template_set_management') || 'テンプレートセット管理'}</h2>
                    <p className='text-sm text-gray-500 mt-1'>{`登録件数: ${templateSets.length}`}</p>
                </div>
                <Button variant='active' onClick={handleOpenCreate}>
                    新規追加
                </Button>
            </div>

            {templateSets.length === 0 ?
                (
                    <div className='py-16 text-center text-gray-500'>登録されているテンプレートセットがありません</div>
                ) :
                (
                    <div className='overflow-x-auto'>
                        <table className='w-full text-sm'>
                            <thead>
                                <tr className='border-b border-gray-200 text-left'>
                                    <th className='py-2 px-3'>ID</th>
                                    <th className='py-2 px-3'>名称</th>
                                    <th className='py-2 px-3'>slug</th>
                                    <th className='py-2 px-3'>tier preset</th>
                                    <th className='py-2 px-3'>監修者必須</th>
                                    <th className='py-2 px-3'>active</th>
                                    <th className='py-2 px-3 w-[100px]'>操作</th>
                                </tr>
                            </thead>
                            <tbody>
                                {templateSets.map(ts => (
                                    <tr key={ts.id} className='border-b border-gray-100 hover:bg-gray-50'>
                                        <td className='py-2 px-3 text-gray-500'>{ts.id}</td>
                                        <td className='py-2 px-3 font-medium'>{ts.name}</td>
                                        <td className='py-2 px-3 font-mono text-xs'>{ts.slug}</td>
                                        <td className='py-2 px-3'>{ts.tier_preset?.name ?? '-'}</td>
                                        <td className='py-2 px-3'>{ts.supervisor_required ? '✓' : ''}</td>
                                        <td className='py-2 px-3'>{ts.is_active ? '✓' : '×'}</td>
                                        <td className='py-2 px-3'>
                                            <div className='flex gap-2'>
                                                <Button variant='outline' size='sm' onClick={() => handleOpenEdit(ts)}>
                                                    <Pencil className='w-4 h-4' />
                                                </Button>
                                                <Button
                                                    variant='outline'
                                                    size='sm'
                                                    onClick={() => handleDelete(ts)}
                                                    className='text-red-600 hover:text-red-800'
                                                >
                                                    <Trash2 className='w-4 h-4' />
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

            <MotifTemplateSetFormSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                mode={sheetMode}
                templateSet={editTarget}
                onSuccess={fetchList}
            />
        </div>
    );
});
